// [#251] Template edit — for draft templates that haven't been certified
// yet. Reachable from /library/:templateId/edit and from the per-row
// Edit action on /library for admins.
//
// Scope (v1, this PR):
//   - draft templates: prefill from useAdminTemplate, edit content +
//     metadata, save in-place via submitDraft, certify when ready.
//   - certified / re_certified / under_review / deprecated: render a
//     read-only banner explaining why edit isn't available and what to
//     do instead (Flag → re-attest for certified; nothing for the
//     others).
//
// Fork-on-save semantics for editing CERTIFIED templates (handoff §1
// decision 2) is deliberately out of scope here — needs server-side
// version-bump support that doesn't exist yet. Filed as a follow-up.
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { TALENT_TIERS } from "../lib/talentTier";
import {
  Button,
  EditorFrame,
  PageHead,
  PageLoading,
} from "../components";
import { EditTagsModal } from "../components/templates/EditTagsModal";
import { withoutVariableMarks } from "../components/templates/variableHighlight";
import {
  hasMarkerBoundaries,
  slotsFromMarkerBoundaries,
  stripMarkerBoundaries,
} from "../components/templates/variableMarkers";
import { useVariableMarks } from "../components/templates/useVariableMarks";
import { getApiErrorMessage } from "../data/api";
import {
  describeVariablesOutcome,
  useAdminTemplate,
  useCloneTemplateAsDraft,
  type CertifiedTemplateRow,
} from "../data/templatesQuery";
import {
  useCertifyL1Template,
  useSubmitL1Draft,
} from "../data/l1TemplatesQuery";
import {
  useCertifyL2Template,
  useSubmitL2Draft,
} from "../data/l2TemplatesQuery";

const DEAL_TYPES: ReadonlyArray<{
  value: CertifiedTemplateRow["dealType"];
  label: string;
}> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{
  value: CertifiedTemplateRow["guild"];
  label: string;
}> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS: ReadonlyArray<{
  value: CertifiedTemplateRow["tier"];
  label: string;
}> = [
  ...TALENT_TIERS,
];

export function TemplateEdit(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const session = useSession();
  const navigate = useNavigate();

  // Layer is SCOPE, not authz: platform staff (studioId null) operate on the
  // L1 platform library; anyone with a studio operates on their L2 tenant lib.
  const layer: "L1" | "L2" = session?.studioId == null ? "L1" : "L2";

  const tenantId =
    layer === "L2" && session?.studioId != null
      ? `tenant-${session.studioId}`
      : "";

  const { data, isLoading, isError, error } = useAdminTemplate(templateId, tenantId);

  // Session resolves asynchronously after sign-in. While it's still
  // null, hold on the loading state — bouncing to /library here would
  // race the auth boot and redirect logged-in admins.
  if (session === null) {
    return (
      <PageLoading
        eyebrow="Templates · edit"
        sub="Resolving session."
      />
    );
  }

  if (
    !sessionCan(session, "template:author", { layer }) ||
    (layer === "L2" && tenantId.length === 0)
  ) {
    return <Navigate to="/library" replace />;
  }

  // [S-685] Deliberate exception to "render PageHead unconditionally":
  // this screen has no static title to hoist. DraftEditor (below) has no
  // page title at all by design (a prior commit dropped it as boilerplate
  // eating vertical space the document needed) — showing one only during
  // loading would flicker in and back out. ReadOnlyState's title names the
  // fetched template, so it can't be known before this resolves either.
  if (isLoading || data === undefined) {
    return (
      <PageLoading
        eyebrow="Templates · edit"
        sub="Fetching template."
        label="Loading template…"
      />
    );
  }

  if (isError) {
    return (
      <PageHead
        eyebrow="Templates · edit"
        title="Template not available"
        sub={getApiErrorMessage(error) ?? "The admin API returned an error."}
      />
    );
  }

  // Layer-and-tenant gate. Server enforces too — this is for UX.
  if (
    data.layerSource === "L1" &&
    !sessionCan(session, "template:author", { layer: "L1" })
  ) {
    return <Navigate to={`/library/${encodeURIComponent(data.id)}`} replace />;
  }
  if (
    data.layerSource === "L2" &&
    data.tenantId !== tenantId &&
    !sessionCan(session, "template:author", { layer: "L2" })
  ) {
    return <Navigate to="/library" replace />;
  }

  // Only drafts are editable in this PR. Other states get a read-only
  // banner that explains the next step.
  if (data.lifecycleState !== "draft") {
    return (
      <ReadOnlyState
        template={data}
        onBack={() => navigate(`/library/${encodeURIComponent(data.id)}`)}
      />
    );
  }

  return <DraftEditor template={data} tenantId={tenantId} />;
}

function DraftEditor({
  template,
  tenantId,
}: {
  template: CertifiedTemplateRow;
  tenantId: string;
}): JSX.Element {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>(template.content);
  // [S-633] All metadata/tags (title, dealType, guild, tier, anchorCBA,
  // agreement form) are edited through the shared EditTagsModal — the single,
  // consistent tag editor used on the detail + management screens. It PATCHes
  // and invalidates the template query, so `template` here stays the source of
  // truth for metadata; this screen owns only the CONTENT. Keeping one metadata
  // editor avoids a second, conflicting write path.
  const [tagsOpen, setTagsOpen] = useState(false);

  // [S-802] A variable is not in the document — a tag set records it beside
  // the template — so an author editing certified text sees a row of
  // underscores with nothing to say which of them a deal will fill.
  //
  // [S-802 follow-up] The editor is handed the variables as MARKER BOUNDARIES,
  // which it turns into markers it transforms through every edit, and the save
  // below reads them back so the tag set follows the author's edit instead of
  // being invalidated by it.
  const marks = useVariableMarks(template);

  // Mark ONCE, when the slots arrive and the author has not started typing:
  // re-marking under a cursor would move it.
  useEffect(() => {
    if (marks.slots.length === 0 || hasMarkerBoundaries(content) || content !== template.content) {
      return;
    }
    setContent(marks.markForEditing(template.content));
  }, [marks, template.content, content]);

  // Snapshot of the last-saved CONTENT, so the sticky bar can show an honest
  // unsaved / saving / saved indicator without depending on the query refetch.
  const [savedContent, setSavedContent] = useState(template.content);

  // [S-802 follow-up] What the last save did to the template's variables —
  // from this screen's own save, or carried in by the detail page's inline
  // edit, which lands the author here on a brand-new draft.
  const arrivedWith = (useLocation().state as { variablesNote?: string } | null)
    ?.variablesNote;
  const [variablesNote, setVariablesNote] = useState<string | null>(
    arrivedWith ?? null
  );

  // If the query rehydrates (cache or a post-retag refetch), resync content.
  useEffect(() => {
    setContent(template.content);
    setSavedContent(template.content);
  }, [template.id]);

  const layer = template.layerSource;
  const submitL1 = useSubmitL1Draft();
  const submitL2 = useSubmitL2Draft(tenantId);
  const certifyL1 = useCertifyL1Template();
  const certifyL2 = useCertifyL2Template(tenantId);
  const submitDraft = layer === "L1" ? submitL1 : submitL2;
  const certify = layer === "L1" ? certifyL1 : certifyL2;

  /** What is actually stored: the author's document, with nothing of the
   *  marking in it. `withoutVariableMarks` still runs because a template saved
   *  before this change can carry the old `<span class="tv-mark">` form. */
  const documentOf = (html: string): string =>
    withoutVariableMarks(stripMarkerBoundaries(html));

  const handleSave = (): void => {
    if (content.trim().length === 0) return;
    // Read the markers back out of what the editor produced. `slots` are
    // offsets into the document as it is about to be stored, so they and the
    // content cannot describe different texts.
    const read = slotsFromMarkerBoundaries(content);
    const reanchored =
      marks.slots.length === 0
        ? null
        : {
            target: marks.source,
            slots: read.slots.map((s) => ({ id: s.id, start: s.start, end: s.end })),
          };
    // Carry the template's CURRENT metadata through unchanged (the retag modal
    // owns edits to it) so a content save never drops a tag — notably the
    // agreement form the long-form promotion depends on. submitDraft re-parses
    // the whole entity, so every field it accepts must be passed explicitly.
    submitDraft.mutate(
      {
        id: template.id,
        title: template.title,
        dealType: template.dealType,
        guild: template.guild,
        tier: template.tier,
        anchorCBA: template.anchorCBA,
        documentForm: template.documentForm,
        content: documentOf(content),
        effectiveFrom: template.effectiveFrom,
        // Where the author left each variable, against the content above.
        ...(reanchored === null ? {} : { variables: reanchored }),
      },
      {
        onSuccess: (saved) => {
          setSavedContent(documentOf(content));
          // [S-802 follow-up] What became of the variables. The server is the
          // only one that knows, and the author is the one who needs to hear
          // it — the alternative is finding out the next time a draft quietly
          // comes out ungrounded.
          setVariablesNote(describeVariablesOutcome(saved.variables));
        },
      }
    );
  };

  const handleCertify = (): void => {
    certify.mutate(
      { templateId: template.id, effectiveFrom: new Date().toISOString() },
      {
        onSuccess: () => {
          navigate(`/library/${encodeURIComponent(template.id)}`);
        },
      }
    );
  };

  const submitError = getApiErrorMessage(submitDraft.error) ?? undefined;
  const certifyError = getApiErrorMessage(certify.error) ?? undefined;

  // The marking is not an edit: compare the DOCUMENT with what was saved.
  const isDirty = documentOf(content) !== savedContent;

  // What the sticky bar surfaces (null = nothing yet — a pristine load).
  const saveState: "saving" | "unsaved" | "saved" | null = submitDraft.isPending
    ? "saving"
    : isDirty
      ? "unsaved"
      : submitDraft.isSuccess
        ? "saved"
        : null;

  return (
    <div className="doc-route">
      {/* Page chrome above the frame: just the compact tag/actions bar — the
          "Edit draft template" header was obvious boilerplate eating vertical
          space, so it's dropped in favor of showing more of the document. */}
      <div className="doc-route__chrome tpl-author tpl-author--edit">
        {/* One slim meta bar: the template's tags summarized as read-only text
            plus an "Edit tags" button (title / deal type / guild / tier /
            agreement form are all edited in the shared modal), with the
            save-state indicator + content actions pushed right. */}
        <div className="tpl-author__meta">
          <span className="tpl-author__meta__lbl">Tags</span>
          <div className="tpl-author__meta__row tpl-author__meta__row--tags">
            <span className="tpl-author__tags" data-testid="tpl-edit-tags-summary">
              {template.title != null && template.title.length > 0 ? (
                <b>{template.title}</b>
              ) : (
                <i>Untitled</i>
              )}
              {` · ${labelOf(DEAL_TYPES, template.dealType)}`}
              {` · ${labelOf(GUILDS, template.guild)}`}
              {` · ${labelOf(TIERS, template.tier)}`}
              {template.documentForm != null &&
                ` · ${template.documentForm === "long_form" ? "Long form" : "Deal memo"}`}
            </span>
            <Button
              variant="ghost"
              className="btn--s"
              onClick={() => setTagsOpen(true)}
              data-testid="tpl-edit-tags"
            >
              Edit tags
            </Button>
          </div>

          <div className="tpl-author__actions">
            {saveState !== null && (
              <span
                className={`tpl-author__savestate tpl-author__savestate--${saveState}`}
                aria-live="polite"
                data-testid="tpl-edit-savestate"
              >
                <span className="dot" aria-hidden="true" />
                {saveState === "saving"
                  ? "Saving…"
                  : saveState === "unsaved"
                    ? "Unsaved changes"
                    : "Saved"}
              </span>
            )}
            <Button
              variant="ghost"
              className="btn--s"
              onClick={handleSave}
              disabled={
                submitDraft.isPending || content.trim().length === 0 || !isDirty
              }
              data-testid="tpl-edit-save"
            >
              {submitDraft.isPending ? "Saving…" : "Save draft"}
            </Button>
            <Button
              variant="primary"
              className="btn--s"
              onClick={handleCertify}
              disabled={
                certify.isPending ||
                submitDraft.isPending ||
                content.trim().length === 0
              }
              data-testid="tpl-edit-certify"
            >
              {certify.isPending ? "Certifying…" : "Certify template"}
            </Button>
          </div>
        </div>

        {variablesNote !== null && (
          <div
            className="tpl-author__note"
            role="status"
            aria-live="polite"
            data-testid="tpl-edit-variables-note"
          >
            {variablesNote}
          </div>
        )}
        {submitError !== undefined && (
          <div className="tpl-author__err" role="alert">
            Save failed — {submitError}
          </div>
        )}
        {certifyError !== undefined && (
          <div className="tpl-author__err" role="alert">
            Certify failed — {certifyError}
          </div>
        )}
      </div>

      {/* The document frame: the editor fills it, its toolbar is sticky,
          its editable is the single scroller, and the pager sits in the
          footer — identical to the read-only view frame. */}
      <EditorFrame
        value={content}
        onChange={setContent}
        placeholder="Template text — edit any clauses that need refinement."
        ariaLabel="Template content editor"
        pagerMeta={`Draft · ${layer}`}
      />

      {/* [S-633] Full tag editor — title, deal type, guild, tier, anchor CBA,
          and agreement form — the same modal used from the detail + management
          screens. It PATCHes + invalidates, so the summary above refreshes. */}
      <EditTagsModal
        template={tagsOpen ? template : null}
        onClose={() => setTagsOpen(false)}
      />
    </div>
  );
}

// Look up a select-option label for a stored value (falls back to the raw value
// / an em dash) — used by the read-only tag summary in the edit meta bar.
function labelOf<T extends string | null>(
  opts: ReadonlyArray<{ value: T; label: string }>,
  value: T
): string {
  return opts.find((o) => o.value === value)?.label ?? String(value ?? "—");
}

function ReadOnlyState({
  template,
  onBack,
}: {
  template: CertifiedTemplateRow;
  onBack: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const state = template.lifecycleState;
  const explanation = explanationFor(state);

  // Capability gates for clone-into-draft: template:author on the L1
  // platform library vs the matching L2 tenant library. We hide the
  // CTA when neither matches so the user sees the read-only banner
  // without a button they can't use (server would 403 anyway).
  const isL1 = template.layerSource === "L1";
  const canCloneL1 = isL1 && sessionCan(session, "template:author", { layer: "L1" });
  const canCloneL2 =
    !isL1 &&
    sessionCan(session, "template:author", { layer: "L2" }) &&
    template.tenantId !== null &&
    session?.studioId !== null &&
    session?.studioId !== undefined &&
    `tenant-${session.studioId}` === template.tenantId;
  const canClone = canCloneL1 || canCloneL2;

  const cloner = useCloneTemplateAsDraft();
  const handleClone = (): void => {
    if (!canClone || cloner.isPending) return;
    cloner.clone(template, (newId) =>
      navigate(`/library/${encodeURIComponent(newId)}/edit`)
    );
  };

  return (
    <>
      <PageHead
        eyebrow={`Templates · ${template.layerSource}`}
        title={
          <>
            Edit isn't available for <em>{state}</em> templates.
          </>
        }
        sub={explanation.body}
      />
      <section
        className="tpl-author tpl-author--readonly"
        data-testid="tpl-edit-readonly"
      >
        <p>{explanation.next}</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canClone && (
            <Button
              variant="primary"
              onClick={handleClone}
              disabled={cloner.isPending}
              data-testid="tpl-edit-clone"
            >
              {cloner.isPending ? "Cloning…" : "Clone into new draft"}
            </Button>
          )}
          <Button variant="ghost" onClick={onBack} data-testid="tpl-edit-back">
            Back to template
          </Button>
        </div>
        {cloner.error !== null && (
          <div className="tpl-author__err" role="alert">
            Clone failed — {cloner.error}
          </div>
        )}
      </section>
    </>
  );
}

function explanationFor(
  state: CertifiedTemplateRow["lifecycleState"]
): { body: string; next: string } {
  switch (state) {
    case "certified":
    case "re_certified":
      return {
        body:
          "A certified template is anchored to active deals. Direct edits would void those anchors.",
        next:
          "Flag the template for review (returns it to under_review). After re-attestation it can be edited via the certify-replacement flow. Saving as an editable copy is on the roadmap as a follow-up.",
      };
    case "under_review":
      return {
        body:
          "An under-review template is locked while a platform attorney re-attests it.",
        next:
          "Once re-attestation completes, the template returns to either certified or back to draft for further edits.",
      };
    case "deprecated":
      return {
        body: "Deprecated templates aren't editable.",
        next: "Author a new draft (Templates → + New template) and reference the deprecated template's successor id if you intend a replacement.",
      };
    default:
      return {
        body: "This template can't be edited right now.",
        next: "Return to the template detail page.",
      };
  }
}
